import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://ruby4.gix.com";

let rolesCache = null;

// Fetch roles from API and cache them
export async function fetchRoles() {
  if (rolesCache) return rolesCache;

  try {
    const response = await axios.get(`${API_BASE_URL}/roles`);
    rolesCache = response.data;
    return rolesCache;
  } catch (error) {
    console.error("Error fetching roles:", error);
    return [];
  }
}

// Get role display name (PT-BR) by name_en
export function getRoleDisplayName(nameEn) {
  if (!nameEn || !rolesCache) return nameEn || "-";

  const role = rolesCache.find((r) => r.name_en === nameEn);
  return role ? role.name : nameEn;
}

// Get all roles formatted for select dropdowns
export function getRolesForSelect() {
  if (!rolesCache) return [];

  return rolesCache
    .filter((role) => role.active)
    .map((role) => ({
      value: role.name_en, // Use name_en for logic
      label: role.name, // Use name for display (PT-BR)
    }));
}

// Formatters
export function booleanToSimNao(value) {
  if (value === true) return "Sim";
  if (value === false) return "Não";
  return "-";
}

export function formatBrazilDateTime(value) {
  if (!value) return "-";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    const datePart = new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);

    const timePart = new Intl.DateTimeFormat("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);

    return `${datePart} às ${timePart}`;
  } catch {
    return "-";
  }
}

export function truncateText(text, maxLength = 50) {
  if (!text) return "-";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}...`;
}

// Display role name - handles both object and string formats
export function normalizeMembershipRole(role) {
  if (!role) return "-";

  // If it's an object with name property (from API)
  if (typeof role === "object" && role.name) {
    return role.name; // Return Portuguese name
  }

  // If it's a string (name_en), get from cache
  if (typeof role === "string") {
    return getRoleDisplayName(role);
  }

  return "-";
}

export function normalizeCivilStatus(value) {
  const map = {
    single: "Solteiro(a)",
    married: "Casado(a)",
    divorced: "Divorciado(a)",
    widowed: "Viúvo(a)",
    not_informed: "-",
  };

  return map[value] || value || "-";
}

export function normalizePixType(value) {
  const map = {
    phone: "Celular",
    email: "E-mail",
    key: "Chave aleatória",
    cpf: "CPF",
  };

  return map[value] || value || "-";
}

export function normalizeAccountType(value) {
  const map = {
    conta_corrente_pf: "Conta Corrente PF",
    conta_poupanca_pf: "Conta Poupança PF",
    conta_corrente_pj: "Conta Corrente PJ",
    conta_poupanca_pj: "Conta Poupança PJ",
  };

  return map[value] || value || "-";
}

export function isImageUrl(url) {
  if (!url) return false;
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(String(url));
}

export function isPdfUrl(url) {
  if (!url) return false;
  return /\.pdf(\?.*)?$/i.test(String(url));
}

// Normalizers
export function normalizeUserFromList(user) {
  if (!user) return {};

  return {
    id: user.id,
    name: user.full_name || "-",
    full_name: user.full_name || "-",
    first_name: user.first_name || "-",
    last_name: user.last_name || "-",
    email: user.email || "-",
    role: normalizeMembershipRole(user.role?.name || user.role || "-"),
    sector: user.sector || "-",
    local: user.local || user.current_org_name || "-",
    agency: user.agency || user.current_org_name || "-",
    bank: user.bank || "-",
    cpf: user.cpf || user.cnpj || "-",
    status: user.status || "-",
    active: booleanToSimNao(user.active),
    blocked: booleanToSimNao(user.blocked),
    project: user.project?.name || user.project || "-",
    createdAt: formatBrazilDateTime(user.created_at || user.createdAt),
    confirmedAt: user.confirmed_at ? formatBrazilDateTime(user.confirmed_at) : null,
    current_fee: user.current_fee !== undefined ? user.current_fee : null,
    balance: user.balance,
    field_values: user.field_values || {},
  };
}

export function normalizeUserFromDetail(user, fallback = {}) {
  if (!user) return normalizeUserFromList(fallback);

  const normalized = normalizeUserFromList(user);

  return {
    ...normalized,
    contact: {
      mobile: user.contact?.mobile || user.mobile || "-",
      home_phone: user.contact?.home_phone || user.home_phone || "-",
      work_phone: user.contact?.work_phone || user.work_phone || "-",
    },
    address: {
      cep: user.address?.cep || "-",
      avenue: user.address?.avenue || "-",
      street_type: user.address?.street_type || "-",
      street: user.address?.street || "-",
      number: user.address?.number || "-",
      neighborhood: user.address?.neighborhood || "-",
      city: user.address?.city || "-",
      state: user.address?.state || "-",
      complement: user.address?.complement || "-",
      landmark: user.address?.landmark || "-",
    },
    personal_info: {
      civil_status: user.personal_info?.civil_status || "-",
      nationality: user.personal_info?.nationality || "-",
      naturalness: user.personal_info?.naturalness || "-",
      father_name: user.personal_info?.father_name || "-",
    },
    payment_info: {
      pix: user.payment_info?.pix || "-",
      pix_type: user.payment_info?.pix_type || "-",
      bank_number: user.payment_info?.bank_number || "-",
      agency: user.payment_info?.agency || "-",
      cc: user.payment_info?.cc || "-",
      cc_digit: user.payment_info?.cc_digit || "-",
      cc_type: user.payment_info?.cc_type || "-",
      cnpj_payment: user.payment_info?.cnpj_payment || "-",
    },
    documents: Array.isArray(user.user_documents)
      ? user.user_documents.map((doc) => ({
          id: doc.id,
          document: {
            name: doc.document?.name || doc.document?.name_en || "Documento",
            name_en: doc.document?.name_en,
          },
          file_url: doc.file_url || "",
          status: doc.status || "pending",
          document_number: doc.document_number,
          regional_advice_kind: doc.regional_advice_kind,
          state: doc.state,
          conclusion_year: doc.conclusion_year,
          emission_date: doc.emission_date,
          expiration_date: doc.expiration_date,
        }))
      : [],
    memberships: Array.isArray(user.memberships)
      ? user.memberships.map((membership) => ({
          id: membership.id,
          org_id: membership.org_id,
          user_id: membership.user_id,
          org_name: membership.org_name,
          role: membership.role,
          role_pt: membership.role_pt,
          status_ids: membership.status_ids,
        }))
      : [],
    updatedAt: formatBrazilDateTime(user.updated_at),
  };
}
export function formatBrlCurrency(value) {
  const number = Number(value);
  if (Number.isNaN(number)) return "-";
  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}